import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Music2, MapPin, Phone, Mail, Globe, ArrowLeft, 
  Instagram, Facebook, Youtube, Twitter, ExternalLink,
  Play, Video, Edit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { API_URL } from '../utils/api';

export default function ArtistDetailPage() {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchArtist();
    checkAdmin();
  }, [artistSlug]);

  const checkAdmin = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsAdmin(payload.email === 'hi@linedancecentral.uk' || payload.email === 'admin@linedancecentral.uk');
      } catch (e) {}
    }
  };

  const fetchArtist = async () => {
    try {
      const response = await fetch(`${API_URL}/api/artists/${artistSlug}`);
      if (response.ok) {
        const data = await response.json();
        setArtist(data);
      } else if (response.status === 404) {
        setError('Artist not found');
      } else {
        setError('Failed to load artist');
      }
    } catch (err) {
      setError('Failed to load artist');
    } finally {
      setLoading(false);
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'youtube': return <Youtube className="w-5 h-5" />;
      case 'twitter': case 'x': return <Twitter className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  const getSocialLabel = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] py-8">
        <div className="container mx-auto px-4">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <Skeleton className="aspect-square rounded-2xl" />
            </div>
            <div className="lg:col-span-2">
              <Skeleton className="h-10 w-3/4 mb-4" />
              <Skeleton className="h-6 w-1/2 mb-6" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <Music2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-stone-700 mb-2">
            {error || 'Artist Not Found'}
          </h1>
          <Link to="/artists">
            <Button variant="outline" className="mt-4 rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Artists
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Back link */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <Link 
            to="/artists" 
            className="inline-flex items-center text-stone-600 hover:text-violet-600 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Artists
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Image & Contact */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Image */}
            <Card className="overflow-hidden bg-white border-stone-200 shadow-sm rounded-2xl">
              <div className="aspect-square bg-gradient-to-br from-violet-100 to-purple-50">
                {artist.image_url ? (
                  <img
                    src={artist.image_url}
                    alt={artist.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music2 className="w-24 h-24 text-violet-300" />
                  </div>
                )}
              </div>
            </Card>

            {/* Contact Info */}
            <Card className="bg-white border-stone-200 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-stone-800">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {artist.location && (
                  <div className="flex items-center text-stone-600">
                    <MapPin className="w-5 h-5 mr-3 text-violet-500 flex-shrink-0" />
                    <span>{artist.location}</span>
                  </div>
                )}
                {artist.phone && (
                  <a 
                    href={`tel:${artist.phone}`}
                    className="flex items-center text-stone-600 hover:text-violet-600 transition-colors"
                  >
                    <Phone className="w-5 h-5 mr-3 text-violet-500 flex-shrink-0" />
                    <span>{artist.phone}</span>
                  </a>
                )}
                {artist.email && (
                  <a 
                    href={`mailto:${artist.email}`}
                    className="flex items-center text-stone-600 hover:text-violet-600 transition-colors"
                  >
                    <Mail className="w-5 h-5 mr-3 text-violet-500 flex-shrink-0" />
                    <span className="truncate">{artist.email}</span>
                  </a>
                )}
                {artist.website && (
                  <a 
                    href={artist.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-stone-600 hover:text-violet-600 transition-colors"
                  >
                    <Globe className="w-5 h-5 mr-3 text-violet-500 flex-shrink-0" />
                    <span className="truncate">Visit Website</span>
                    <ExternalLink className="w-4 h-4 ml-1 flex-shrink-0" />
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Social Links */}
            {Object.keys(artist.social_links || {}).length > 0 && (
              <Card className="bg-white border-stone-200 shadow-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-stone-800">Social Media</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(artist.social_links).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-3 rounded-xl hover:bg-violet-50 text-stone-600 hover:text-violet-600 transition-colors"
                    >
                      {getSocialIcon(platform)}
                      <span className="ml-3 font-medium">{getSocialLabel(platform)}</span>
                      <ExternalLink className="w-4 h-4 ml-auto text-stone-400" />
                    </a>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  {artist.is_featured && (
                    <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full px-3">Featured Artist</Badge>
                  )}
                </div>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/dashboard/edit-artist/${artist.artist_id}`)}
                    className="gap-2 rounded-full border-stone-300 text-stone-600 hover:bg-violet-50 hover:text-violet-600 hover:border-violet-300"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Profile
                  </Button>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {artist.stage_name || artist.name}
              </h1>
              {artist.stage_name && artist.name !== artist.stage_name && (
                <p className="text-lg text-stone-500">{artist.name}</p>
              )}
            </div>

            {/* Performance Types & Genres */}
            <div className="flex flex-wrap gap-6">
              {artist.performance_types?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-stone-500 mb-2">Performance Type</p>
                  <div className="flex flex-wrap gap-2">
                    {artist.performance_types.map(type => (
                      <Badge key={type} className="bg-violet-100 text-violet-700 border-0 rounded-full px-3 py-1">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {artist.genres?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-stone-500 mb-2">Genres</p>
                  <div className="flex flex-wrap gap-2">
                    {artist.genres.map(genre => (
                      <Badge key={genre} variant="outline" className="border-stone-300 text-stone-600 rounded-full px-3 py-1">
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bio */}
            <Card className="bg-white border-stone-200 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-stone-800">About</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-stone-600 whitespace-pre-wrap leading-relaxed text-base">
                  {artist.bio}
                </p>
              </CardContent>
            </Card>

            {/* Video Links */}
            {artist.video_urls?.length > 0 && (
              <Card className="bg-white border-stone-200 shadow-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-stone-800">
                    <Video className="w-5 h-5 text-violet-500" />
                    Performance Videos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {artist.video_urls.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-4 rounded-xl border border-stone-200 hover:bg-violet-50 hover:border-violet-300 transition-colors"
                      >
                        <Play className="w-5 h-5 text-violet-500 mr-3" />
                        <span className="text-stone-600 truncate flex-1 font-medium">
                          Video {index + 1}
                        </span>
                        <ExternalLink className="w-4 h-4 text-stone-400" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Music Links */}
            {artist.music_urls?.length > 0 && (
              <Card className="bg-white border-stone-200 shadow-sm rounded-2xl">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-stone-800">
                    <Music2 className="w-5 h-5 text-violet-500" />
                    Music Samples
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {artist.music_urls.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-4 rounded-xl border border-stone-200 hover:bg-violet-50 hover:border-violet-300 transition-colors"
                      >
                        <Music2 className="w-5 h-5 text-violet-500 mr-3" />
                        <span className="text-stone-600 truncate flex-1 font-medium">
                          Track {index + 1}
                        </span>
                        <ExternalLink className="w-4 h-4 text-stone-400" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
